import React, { useEffect, useState } from 'react';
import ClockTimeField from '../Common/ClockTimeField';
import PreferredDateField from '../Common/PreferredDateField';
import { PlaceSearch, ShootMap } from '../Common/ShootLocationField';
import { API_BASE_URL } from '../../utils/apiBase';
import { StoredQuote } from '../../utils/quoteRecord';

interface ShootRequestEditorProps {
  quote: StoredQuote;
  onSaved: (quote: StoredQuote) => void;
}

/**
 * Date, time and place on a quotation that is not yet accepted.
 * A confirmed booking is edited separately, after acceptance.
 */
const ShootRequestEditor: React.FC<ShootRequestEditorProps> = ({ quote, onSaved }) => {
  const place = quote.preferredLocation || {};
  const [date, setDate] = useState(quote.preferredDate || '');
  const [time, setTime] = useState((quote.preferredTime || '').slice(0, 5));
  const [name, setName] = useState(place.name || '');
  const [address, setAddress] = useState(place.address || '');
  const [mapsUrl, setMapsUrl] = useState(place.mapsUrl || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingTime, setEditingTime] = useState(false);

  useEffect(() => {
    const next = quote.preferredLocation || {};
    setEditingTime(false);
    setDate(quote.preferredDate || '');
    setTime((quote.preferredTime || '').slice(0, 5));
    setName(next.name || '');
    setAddress(next.address || '');
    setMapsUrl(next.mapsUrl || '');
  }, [quote.id, quote.preferredDate, quote.preferredTime, quote.preferredLocation]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/shoot`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preferredDate: date,
          preferredTime: time,
          preferredLocation: { name, address, mapsUrl },
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.error || 'The date and place could not be saved.');
        return;
      }
      onSaved(body as StoredQuote);
    } catch {
      setError('The date and place could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="studio-panel" data-testid="shoot-request-editor">
      <p className="studio-note">
        This is the date and place on the quotation. Change them here before the client accepts.
        A confirmed booking starts after acceptance.
      </p>
      <PreferredDateField
        value={date}
        onChange={setDate}
        allowPast
        label="Preferred date"
        dateLabel="Date in mind"
        laterLabel="Decide later"
      />
      <div className="studio-contact-grid">
        {editingTime ? (
          <ClockTimeField
            label="Preferred time"
            ariaLabel="Preferred time"
            value={time}
            onChange={setTime}
            autoOpen
            onDismiss={() => setEditingTime(false)}
          />
        ) : (
          <div className="studio-quote-time-rest">
            <span>{time || 'No time yet'}</span>
            <button type="button" className="studio-text-action" onClick={() => setEditingTime(true)}>
              {time ? 'Edit time' : 'Set a time'}
            </button>
          </div>
        )}
      </div>
      <PlaceSearch onSelect={(selected) => {
        setName(selected.name);
        setAddress(selected.address);
        setMapsUrl(selected.mapsUrl);
      }} />
      <div className="studio-contact-grid">
        <label>
          Place
          <input aria-label="Place" value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label>
          Address
          <input aria-label="Address" value={address} onChange={(event) => setAddress(event.target.value)} />
        </label>
      </div>
      <ShootMap mapsUrl={mapsUrl} label={name || address} />
      {mapsUrl && /^https:\/\//i.test(mapsUrl) ? (
        <p><a href={mapsUrl} target="_blank" rel="noreferrer">Open in Google Maps</a></p>
      ) : null}
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      <div className="studio-toolbar">
        <button type="button" className="studio-primary" onClick={save} disabled={saving} data-testid="save-shoot-request">
          {saving ? 'Saving…' : 'Save date and place'}
        </button>
      </div>
    </div>
  );
};

export default ShootRequestEditor;
